/**
 * Form Event Emitter
 * Single-responsibility class for managing form event listeners and dispatching events.
 * Extracted from FormEngine to satisfy SRP.
 */

import { getAtlasLogger } from '@atlas-forms/types-js';
import type { FormEngineEventType, FormEngineEvent } from './form.engine';

/**
 * Manages registration and dispatch of form events with per-listener error isolation.
 */
export class FormEventEmitter {
  private listeners: Map<FormEngineEventType, Set<(event: FormEngineEvent) => void>>;

  constructor() {
    this.listeners = new Map();
  }

  /**
   * Subscribe to a form event.
   * @returns Unsubscribe function
   */
  on(eventType: FormEngineEventType, listener: (event: FormEngineEvent) => void): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(listener);

    return () => {
      this.listeners.get(eventType)?.delete(listener);
    };
  }

  /**
   * Emit an event to all subscribers of that type.
   * Each listener runs in isolation — a failing listener does not prevent others from running.
   */
  emit(type: FormEngineEventType, data: Partial<FormEngineEvent> = {}): void {
    const listeners = this.listeners.get(type);
    if (!listeners) return;

    const event: FormEngineEvent = {
      type,
      timestamp: Date.now(),
      ...data,
    };

    for (const listener of listeners) {
      try {
        listener(event);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        const errorDetails = error instanceof Error ? error.stack : JSON.stringify(error);

        getAtlasLogger().error(
          `[FormEventEmitter] Listener error for '${type}': ${errorMsg}`,
          { field: event.fieldId ?? 'unknown', details: errorDetails }
        );

        // Emit error event to notify app of listener failures (avoid infinite recursion)
        if (type !== 'error') {
          this.emit('error', {
            error: `Listener failed for ${type} event: ${errorMsg}`,
          });
        }
      }
    }
  }

  /**
   * Remove all listeners for all event types.
   */
  clear(): void {
    this.listeners.clear();
  }
}
